/**
 * MEMCORE Modul B (Labor) — „Mein Gedächtnis“ (UI Hybrid B, SKALIERUNG §7.6).
 *
 * Liste der eigenen aktiven Vermerke (PB-Lesepfad, Rule scoped auf
 * @request.auth.id). Skills manuell anlegen (Freitext ± Chip), Bearbeiten =
 * neue Version (Curator supersede), Löschen = Tombstone. Schreiben läuft
 * ausschließlich über die Curator-Schreibstraße.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  BookMarked, Loader2, Pencil, Trash2, RefreshCw, AlertTriangle, CheckCircle2, Sparkles, X, Plus,
} from 'lucide-react';
import { getPortalPocketBaseUrl, getPortalPbToken } from '../../../constants';
import {
  type MemVermerk,
  type MemcoreContext,
  type SkillChipId,
  SKILL_CHIPS,
  curatorFehlertext,
  isSkillChipVermerk,
  isSkillVermerk,
  listMeineVermerke,
  memcoreSkillSpeichern,
  memcoreSupersede,
  memcoreTombstone,
  shouldSaveMemoryDraft,
  skillChipIdFromQuelleRef,
} from '../memcore';
import { portalBrainSurfaceClass } from '../portalAppearance';
import { usePortalAppearance } from '../portalAppearanceContext';

export interface MemoryViewProps {
  ctx: MemcoreContext;
  curatorUrl: string;
  /** Onboarding-Interview erneut starten (Erststart-Dialog im CustomerPortal). */
  onStartOnboarding: () => void;
}

function formatDatum(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

const VERTRAUEN_BADGE: Record<string, string> = {
  belegt: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  abgeleitet: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  vermutet: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
};

/** Schwarzer Grund, feine lila Kante — wie die Aktenliste und der Kalender. */
const GEDAECHTNIS_GROUND = 'bg-[#07070a]';
const GEDAECHTNIS_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
/** Knopf wie Neue Akte und Öffnen. */
const GEDAECHTNIS_BTN =
  'inline-flex shrink-0 items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-45';
/** Icon-Knopf wie die Leiste im Hauptreiter. */
const GEDAECHTNIS_ICON =
  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-45';
const GEDAECHTNIS_FIELD =
  'w-full rounded-md border border-violet-400/30 bg-[#07070a] px-3 py-2 text-sm leading-relaxed text-slate-100 placeholder:text-slate-600 focus:border-violet-400 focus:outline-none';

/**
 * Dieselbe Würfel-Drehung wie Arko ON/off: rotateX, 500ms, Perspektive 280.
 * Nur wo ein Feld auf- und zuklappt.
 */
function GedaechtnisCube({
  open,
  front,
  back,
}: {
  open: boolean;
  front: React.ReactNode;
  back: React.ReactNode;
}) {
  const face =
    'absolute inset-0 inline-flex h-full w-full items-center justify-center rounded-md [backface-visibility:hidden]';
  return (
    <span className="relative block h-7 w-7 shrink-0 [perspective:280px]" aria-hidden>
      <span
        className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d]"
        style={{ transform: open ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
      >
        <span className={`${face} border border-violet-400/40 bg-violet-600/30 text-violet-200`}>
          {front}
        </span>
        <span className={`${face} bg-violet-600 text-white`} style={{ transform: 'rotateX(180deg)' }}>
          {back}
        </span>
      </span>
    </span>
  );
}

function skillBadgeLabel(v: MemVermerk): string | null {
  if (!isSkillVermerk(v)) return null;
  const chipId = skillChipIdFromQuelleRef(v.quelle_ref);
  if (chipId) {
    const chip = SKILL_CHIPS.find((c) => c.id === chipId);
    return chip ? `Arbeitsweise · ${chip.label}` : 'Arbeitsweise';
  }
  return 'Arbeitsweise';
}

export const MemoryView: React.FC<MemoryViewProps> = ({ ctx, curatorUrl, onStartOnboarding }) => {
  const appearance = usePortalAppearance();
  const brainLight = portalBrainSurfaceClass(appearance);
  const [vermerke, setVermerke] = useState<MemVermerk[]>([]);
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState('');
  const [hinweis, setHinweis] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [skillOpen, setSkillOpen] = useState(true);
  const [skillText, setSkillText] = useState('');
  const [skillChip, setSkillChip] = useState<SkillChipId | null>(null);
  const [skillBusy, setSkillBusy] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    setFehler('');
    try {
      const token = getPortalPbToken();
      if (!token) {
        throw new Error('Keine aktive Portal-Sitzung — bitte neu anmelden.');
      }
      const items = await listMeineVermerke(getPortalPocketBaseUrl(), token, ctx.userId);
      setVermerke(items);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : 'Gedächtnis konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [ctx.userId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const startEdit = (v: MemVermerk) => {
    setEditId(v.vermerk_id);
    setEditText(v.inhalt);
    setConfirmDeleteId(null);
    setHinweis('');
  };

  const speichereEdit = async (v: MemVermerk) => {
    const neu = editText.trim();
    if (!neu || neu === v.inhalt) {
      setEditId(null);
      return;
    }
    setBusyId(v.vermerk_id);
    const r = await memcoreSupersede(curatorUrl, ctx, v.vermerk_id, neu, v.quelle_ref);
    setBusyId(null);
    if (r.status === 'ok') {
      setHinweis('Als neue Fassung gespeichert.');
      setEditId(null);
      void laden();
    } else {
      setFehler(curatorFehlertext(r));
    }
  };

  const loesche = async (v: MemVermerk) => {
    setBusyId(v.vermerk_id);
    const r = await memcoreTombstone(curatorUrl, ctx, v.vermerk_id);
    setBusyId(null);
    setConfirmDeleteId(null);
    if (r.status === 'ok') {
      setHinweis('Eintrag gelöscht.');
      setVermerke((prev) => prev.filter((x) => x.vermerk_id !== v.vermerk_id));
    } else {
      setFehler(curatorFehlertext(r));
    }
  };

  const speichereSkill = async () => {
    if (!shouldSaveMemoryDraft(skillText)) return;
    const text = skillText.trim();
    setSkillBusy(true);
    setFehler('');
    const r = await memcoreSkillSpeichern(curatorUrl, ctx, text, skillChip);
    setSkillBusy(false);
    if (r.status === 'ok' || r.status === 'duplikat') {
      const chipLabel = skillChip
        ? SKILL_CHIPS.find((c) => c.id === skillChip)?.label
        : null;
      setHinweis(
        r.status === 'duplikat'
          ? 'Bereits vorhanden — kein Duplikat angelegt.'
          : chipLabel
            ? `Eintrag gespeichert („${chipLabel}“).`
            : 'Eintrag gespeichert.',
      );
      setSkillText('');
      setSkillChip(null);
      setSkillOpen(false);
      void laden();
    } else {
      setFehler(curatorFehlertext(r));
    }
  };

  return (
    <div
      className={`w-full min-w-0 max-w-none flex flex-1 flex-col min-h-0 h-full overflow-y-auto gap-3 px-3 py-3 ${GEDAECHTNIS_GROUND} ${brainLight}`}
      data-testid="portal-gedaechtnis-pane"
    >
      <header className={`flex flex-shrink-0 items-start justify-between gap-3 px-3.5 py-3 ${GEDAECHTNIS_EDGE}`}>
        <div>
          <h2 className="m-0 flex items-center gap-2 text-sm font-semibold text-white">
            <BookMarked className="h-4 w-4 text-violet-300" aria-hidden />
            Brain
          </h2>
          <p className="m-0 mt-0.5 text-[11px] leading-relaxed text-slate-500">
            Hinweise, wie ARKO für Sie formulieren soll — nur Ihr Sitz, keine Akten.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setSkillOpen((o) => !o);
              setFehler('');
              setHinweis('');
            }}
            className="relative h-7 w-7 shrink-0 p-0"
            title={skillOpen ? 'Schließen' : 'Arbeitsweise merken'}
            aria-label="Arbeitsweise merken"
            aria-expanded={skillOpen}
          >
            <GedaechtnisCube
              open={skillOpen}
              front={<Plus className="h-3.5 w-3.5" aria-hidden />}
              back={<X className="h-3.5 w-3.5" aria-hidden />}
            />
          </button>
          <button
            type="button"
            onClick={onStartOnboarding}
            className={GEDAECHTNIS_BTN}
            title="Onboarding-Interview (erneut) durchlaufen"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Interview
          </button>
          <button
            type="button"
            onClick={() => void laden()}
            disabled={loading}
            className={GEDAECHTNIS_ICON}
            title="Aktualisieren"
            aria-label="Aktualisieren"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden />
          </button>
        </div>
      </header>

      {skillOpen && (
        <div className={`${GEDAECHTNIS_EDGE} px-4 py-3 space-y-3`}>
          <p className="text-xs font-semibold text-slate-200">Eintrag</p>
          <p className="text-[11px] leading-relaxed text-slate-500">
            Text für diesen Sitz. Eine Kategorie ist optional. ARKO liest aktive Einträge bei jeder Antwort mit.
          </p>
          <textarea
            value={skillText}
            onChange={(e) => setSkillText(e.target.value)}
            rows={3}
            placeholder="z. B. Formuliere knapp und ohne Floskeln."
            className={GEDAECHTNIS_FIELD}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Kategorie (optional)</span>
            {SKILL_CHIPS.map((chip) => {
              const selected = skillChip === chip.id;
              return (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => setSkillChip(selected ? null : chip.id)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    selected
                      ? 'border-violet-400/50 bg-violet-500/15 text-violet-100'
                      : 'border-violet-400/25 bg-[#07070a] text-slate-400 hover:border-violet-400/50 hover:text-slate-200'
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setSkillOpen(false);
                setSkillText('');
                setSkillChip(null);
              }}
              disabled={skillBusy}
              className="rounded-lg px-2.5 py-1 text-xs text-slate-400 hover:text-slate-200"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void speichereSkill()}
              disabled={skillBusy || !shouldSaveMemoryDraft(skillText)}
              className={GEDAECHTNIS_BTN}
            >
              {skillBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Speichern
            </button>
          </div>
        </div>
      )}

      {fehler && (
        <div className="flex items-start justify-between gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <span className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            {fehler}
          </span>
          <button type="button" onClick={() => setFehler('')} aria-label="Hinweis schließen">
            <X className="h-3.5 w-3.5 text-rose-300/70 hover:text-rose-200" />
          </button>
        </div>
      )}
      {hinweis && (
        <div className="flex items-start justify-between gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
          <span className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            {hinweis}
          </span>
          <button type="button" onClick={() => setHinweis('')} aria-label="Hinweis schließen">
            <X className="h-3.5 w-3.5 text-emerald-300/70 hover:text-emerald-200" />
          </button>
        </div>
      )}

      {loading && vermerke.length === 0 && (
        <div className={`flex items-center gap-2 px-4 py-6 text-sm text-slate-400 ${GEDAECHTNIS_EDGE}`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          Gedächtnis wird geladen…
        </div>
      )}

      {!loading && !fehler && vermerke.length === 0 && !skillOpen && (
        <div className={`px-4 py-6 text-center ${GEDAECHTNIS_EDGE}`}>
          <p className="text-sm text-slate-300">Noch keine Einträge.</p>
          <p className="mt-1 text-xs text-slate-500">
            Arbeitsweise merken, Interview starten oder im ARKO-Chat per „Session beenden“
            bzw. Daumen hoch speichern.
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setSkillOpen(true)}
              className={GEDAECHTNIS_BTN}
            >
              <Plus className="h-3.5 w-3.5" />
              Arbeitsweise merken
            </button>
            <button
              type="button"
              onClick={onStartOnboarding}
              className={GEDAECHTNIS_BTN}
              title="Onboarding starten"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Interview
            </button>
          </div>
        </div>
      )}

      <ul className="space-y-2">
        {vermerke.map((v) => {
          const busy = busyId === v.vermerk_id;
          const editing = editId === v.vermerk_id;
          const skillLabel = skillBadgeLabel(v);
          return (
            <li
              key={v.id || v.vermerk_id}
              className={`px-4 py-3 ${GEDAECHTNIS_EDGE}`}
            >
              <div className="flex items-center gap-2">
                {skillLabel && (
                  <span
                    className={`rounded-full border px-1.5 py-0 text-[9px] font-semibold tracking-wide ${
                      isSkillChipVermerk(v)
                        ? 'border-violet-500/40 bg-violet-500/10 text-violet-200'
                        : 'border-violet-400/25 bg-[#07070a] text-slate-300'
                    }`}
                    title="ARKO liest aktive Einträge bei jeder Antwort mit"
                  >
                    {skillLabel}
                  </span>
                )}
                {v.vertrauen && (
                  <span
                    className={`rounded-full border px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider ${
                      VERTRAUEN_BADGE[v.vertrauen] ?? 'border-violet-400/25 bg-[#07070a] text-slate-400'
                    }`}
                  >
                    {v.vertrauen}
                  </span>
                )}
                {v.ersetzt && (
                  <span className="text-[9px] text-slate-600">
                    neue Fassung
                  </span>
                )}
                <span className="ml-auto text-[10px] text-slate-600">
                  seit {formatDatum(v.gueltig_von)}
                </span>
                <button
                  type="button"
                  onClick={() => (editing ? setEditId(null) : startEdit(v))}
                  disabled={busy}
                  className="relative h-7 w-7 shrink-0 p-0 disabled:opacity-45"
                  title="Bearbeiten (neue Version)"
                  aria-label="Eintrag bearbeiten"
                  aria-expanded={editing}
                >
                  <GedaechtnisCube
                    open={editing}
                    front={<Pencil className="h-3.5 w-3.5" aria-hidden />}
                    back={<X className="h-3.5 w-3.5" aria-hidden />}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(confirmDeleteId === v.vermerk_id ? null : v.vermerk_id)}
                  disabled={busy}
                  className={busy ? GEDAECHTNIS_ICON : 'relative h-7 w-7 shrink-0 p-0 disabled:opacity-45'}
                  title="Löschen"
                  aria-label="Eintrag löschen"
                  aria-expanded={confirmDeleteId === v.vermerk_id}
                >
                  {busy ? (
                    <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin text-violet-200" aria-hidden />
                  ) : (
                    <GedaechtnisCube
                      open={confirmDeleteId === v.vermerk_id}
                      front={<Trash2 className="h-3.5 w-3.5" aria-hidden />}
                      back={<X className="h-3.5 w-3.5" aria-hidden />}
                    />
                  )}
                </button>
              </div>

              {!editing && (
                <p className="mt-2 text-sm leading-relaxed text-slate-200 whitespace-pre-wrap">{v.inhalt}</p>
              )}

              {editing && (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    className={GEDAECHTNIS_FIELD}
                  />
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditId(null)}
                      disabled={busy}
                      className="rounded-lg px-2.5 py-1 text-xs text-slate-400 hover:text-slate-200"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="button"
                      onClick={() => void speichereEdit(v)}
                      disabled={busy || !editText.trim()}
                      className={GEDAECHTNIS_BTN}
                      title="Als neue Version speichern"
                    >
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" aria-hidden />}
                      Speichern
                    </button>
                  </div>
                </div>
              )}

              {confirmDeleteId === v.vermerk_id && !editing && (
                <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2">
                  <span className="text-xs text-rose-200">
                    Wirklich löschen? ARKO nutzt diesen Vermerk danach nicht mehr.
                  </span>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="rounded px-2 py-1 text-xs text-slate-400 hover:text-slate-200"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="button"
                      onClick={() => void loesche(v)}
                      disabled={busy}
                      className="rounded bg-rose-600 px-2 py-1 text-xs font-semibold text-white hover:bg-rose-500"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MemoryView;
